var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { PaginationEvent, paginationEventType } from '@cof/fs-pagination-core';
import paginationHostStyles from '@cof/fs-pagination-core/lib/pagination.host.lit.min.js';
import paginationStyles from '@cof/fs-pagination-core/lib/pagination.lit.min.js';
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
/**
 * Base class for Pagination components.
 *
 * @event change - Dispatched when the user changes the page.
 */
export class FsPaginationBase extends LitElement {
    constructor() {
        super(...arguments);
        this._pageNumberReplacementString = '{pageNumber}';
        /**
         * Handle the user changing the page.
         *
         * @param newPageNumber The page the user is attempting to navigate to.
         * @param event The native event triggering the change.
         */
        this._onChangePage = (newPageNumber, event) => {
            event.preventDefault();
            const changeEvent = new PaginationEvent(paginationEventType.change, {
                detail: newPageNumber,
                originalTarget: event.target
            });
            this.dispatchEvent(changeEvent);
            if (!changeEvent.defaultPrevented) {
                this.currentPage = newPageNumber;
            }
            return false;
        };
    }
    static get styles() {
        return [paginationStyles, paginationHostStyles];
    }
    /**
     * Builds a href for the provided page number.
     *
     * @param pageNumber - The page number to embed in the href.
     * @protected
     */
    _buildHrefForPageNumber(pageNumber) {
        return this.hrefTemplate.replace(this._pageNumberReplacementString, pageNumber.toString());
    }
    /**
     * Builds the class for direction controls.
     *
     * @param direction The direction the control will send the user.
     * @param disabled Whether the control is disabled.
     * @protected
     */
    _buildDirectionControlClass(direction, disabled) {
        const prefix = 'fs-pagination-';
        const suffix = '-button';
        const base = `${prefix}${direction}${suffix}`;
        return disabled ? `${base} ${base}--disabled` : base;
    }
    /**
     * Format the contents of the direction control tooltip.
     *
     * @param tooltipContent The raw content.
     * @protected
     */
    _formatDirectionControlTooltipContents(tooltipContent) {
        const tooltipContentWords = tooltipContent.split('-');
        const formattedTooltipContentWords = tooltipContentWords.map((tooltipContentWord) => tooltipContentWord.charAt(0).toUpperCase() + tooltipContentWord.slice(1));
        return formattedTooltipContentWords.join(' ');
    }
    /**
     * Gets the next page number according to the direction provided.
     *
     * @param direction The direction the control will send the user.
     * @protected
     */
    _getNextPageNumber(direction) {
        switch (direction) {
            case 'first-page': {
                return 1;
            }
            case 'previous-page': {
                return this.currentPage - 1;
            }
            case 'next-page': {
                return this.currentPage + 1;
            }
            case 'last-page': {
                return this.pageCount;
            }
            default: {
                return this.currentPage;
            }
        }
    }
    /**
     * Render directional buttons.
     *
     * @param direction The direction the control will send the user.
     * @param disabled Whether the control is disabled.
     * @protected
     */
    _renderDirectionControl(direction, disabled = false) {
        const newPageNumber = this._getNextPageNumber(direction);
        const constructedClass = this._buildDirectionControlClass(direction, disabled);
        const tooltipContent = this._formatDirectionControlTooltipContents(direction);
        return html `
      <li class="${constructedClass}">
        ${this.navigationType === 'in-page'
            ? this._renderDirectionControlButton(direction, disabled, tooltipContent, newPageNumber)
            : this._renderDirectionControlAnchor(direction, disabled, tooltipContent, newPageNumber)}
      </li>
    `;
    }
    /**
     * Render the direction control as a button.
     *
     * @param direction The direction the control will send the user.
     * @param disabled Whether the control is disabled.
     * @param tooltipContent The content of the direction control tooltip.
     * @param newPageNumber The new page number to be navigated to.
     * @protected
     */
    _renderDirectionControlButton(direction, disabled, tooltipContent, newPageNumber) {
        const button = html `
      <button
        @click=${(event) => this._onChangePage(newPageNumber, event)}
        ?disabled=${disabled} type="button"
      >
        Go to ${direction} page
      </button>
    `;
        if (disabled) {
            return button;
        }
        return html `
      <fs-pagination-tooltip class="fs-pagination-tooltip" tooltip-content="${tooltipContent}">
        ${button}
      </fs-pagination-tooltip>
    `;
    }
    /**
     * Render the direction control as an anchor.
     *
     * @param direction The direction the control will send the user.
     * @param disabled Whether the control is disabled.
     * @param tooltipContent The content of the direction control tooltip.
     * @param newPageNumber The new page number to be navigated to.
     * @protected
     */
    _renderDirectionControlAnchor(direction, disabled, tooltipContent, newPageNumber) {
        const anchor = html `
      <a
        ?disabled=${disabled}
        href="${this._buildHrefForPageNumber(newPageNumber)}"
      >
        Go to ${direction} page
      </a>
    `;
        if (disabled) {
            return anchor;
        }
        return html `
      <fs-pagination-tooltip class="fs-pagination-tooltip" tooltip-content="${tooltipContent}">
        ${anchor}
      </fs-pagination-tooltip>
    `;
    }
}
__decorate([
    property({ type: String, attribute: 'accessibility-label' })
], FsPaginationBase.prototype, "accessibilityLabel", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsPaginationBase.prototype, "compact", void 0);
__decorate([
    property({ type: Number, attribute: 'current-page' })
], FsPaginationBase.prototype, "currentPage", void 0);
__decorate([
    property({ type: String, attribute: 'href-template' })
], FsPaginationBase.prototype, "hrefTemplate", void 0);
__decorate([
    property({ type: String, attribute: 'navigation-type' })
], FsPaginationBase.prototype, "navigationType", void 0);
__decorate([
    property({ type: Number, attribute: 'page-count' })
], FsPaginationBase.prototype, "pageCount", void 0);
//# sourceMappingURL=PaginationBase.js.map